import { lazy, Suspense, useEffect } from 'react'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { AppLayout } from './AppLayout'
import { RequireSession } from './RequireSession'
import { StoreHydrator } from '@/components/store-hydrator'
import { ErrorBoundary } from '@/components/error-boundary'
import { MENU_ORIGIN } from '@/lib/brand'

/**
 * The route tree, mirroring the Next.js file layout it replaces.
 *
 * The `(app)` route group becomes a layout route: everything nested inside it
 * gets the sidebar, header and StoreHydrator, exactly as `app/(app)/layout.tsx`
 * does today. `/login`, `/signup`, `/terms`, `/privacy`, `/kiosk`, `/display`
 * and `/platform` sit outside it — the same split, written out instead of
 * inferred from folder names.
 *
 * Every page is lazily loaded. Under Next each route was its own chunk for
 * free; here it has to be asked for, and it matters more than usual: the till
 * should not download the accounting module to sell a bottle of water.
 */
const page = (loader: () => Promise<{ default: React.ComponentType }>) => lazy(loader)

const Login      = page(() => import('@/app/login/page'))
const Signup     = page(() => import('@/app/signup/page'))
const Terms      = page(() => import('@/app/terms/page'))
const Privacy    = page(() => import('@/app/privacy/page'))
const DeleteAccount = page(() => import('@/app/delete-account/page'))
const Forgot     = page(() => import('@/app/auth/forgot/page'))
const Reset      = page(() => import('@/app/auth/reset/page'))
const Confirm    = page(() => import('@/app/auth/confirm/page'))
const ScaleCheck = page(() => import('@/app/scale-check/page'))
const ClubRegister = page(() => import('@/app/club/page'))
const ClubCard = page(() => import('@/app/club/card-page'))
const Home       = page(() => import('@/app/page'))
const Kiosk      = page(() => import('@/app/kiosk/page'))
// 1.67 — the waiter's phone (its own full-screen app, outside the shop shell)
const Waiter     = page(() => import('@/components/waiter/waiter-app'))
// 1.67 — the kitchen screen (a locked station device, outside the shop shell)
const Kitchen    = page(() => import('@/components/kitchen/kitchen-screen'))
const Display    = page(() => import('@/app/display/page'))
const Platform   = page(() => import('@/app/platform/page'))

const Pos        = page(() => import('@/app/(app)/pos/page'))
const Warehouse  = page(() => import('@/app/(app)/warehouse/page'))
const Suppliers  = page(() => import('@/app/(app)/suppliers/page'))
const Credit     = page(() => import('@/app/(app)/credit/page'))
const Repairs    = page(() => import('@/app/(app)/repairs/page'))
const Orders     = page(() => import('@/app/(app)/orders/page'))
const Accounting = page(() => import('@/app/(app)/accounting/page'))
const Staff      = page(() => import('@/app/(app)/staff/page'))
const Tools      = page(() => import('@/app/(app)/tools/page'))
const Guide      = page(() => import('@/app/(app)/guide/page'))
const Settings   = page(() => import('@/app/(app)/settings/page'))
const Terminal   = page(() => import('@/app/(app)/terminal/page'))

function RouteFallback() {
  return (
    <div className="flex h-dvh w-full items-center justify-center bg-background">
      <Loader2 className="size-6 animate-spin text-muted-foreground" />
    </div>
  )
}

export function App() {
  return (
    // The last resort: a crash in the shell itself, on /kiosk, /display or a
    // login page — none of which sit inside AppLayout's boundary.
    <ErrorBoundary label="root">
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public — no session required. */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        {/* Google Play's web path for account deletion — public, no login. */}
        <Route path="/delete-account" element={<DeleteAccount />} />

        {/* Password recovery. PUBLIC by definition: whoever opens these
            cannot sign in. */}
        <Route path="/auth/forgot" element={<Forgot />} />
        <Route path="/auth/reset" element={<Reset />} />
        {/* Email confirmation after signup (088). Public for the same reason. */}
        <Route path="/auth/confirm" element={<Confirm />} />
        {/* A scale checked at the seller's before a shop buys it — reads a
            USB scale, touches no shop data, so no login (2026-10-17). */}
        <Route path="/scale-check" element={<ScaleCheck />} />
        {/* 1.63 — the loyalty club: a customer registers from the poster and carries the card; no login */}
        <Route path="/club/:clubCode" element={<ClubRegister />} />
        <Route path="/c/:cardToken" element={<ClubCard />} />

        {/* Signed in, but outside the shop shell (own full-screen chrome). */}
        <Route element={<RequireSession />}>
          {/* The kiosk sells, so it needs the store hydrated — `app/kiosk/
              layout.tsx` wrapped it in StoreHydrator under Next, and the
              cutover dropped that: opened in its own window or reloaded, it
              waited on `isHydrated` for ever (audit, 2026-09-11). */}
          <Route element={<StoreHydrator><Outlet /></StoreHydrator>}>
            <Route path="/kiosk" element={<Kiosk />} />
            <Route path="/waiter" element={<Waiter />} />
            <Route path="/kitchen" element={<Kitchen />} />
          </Route>
          <Route path="/display" element={<Display />} />
          <Route path="/platform" element={<Platform />} />

          {/* The shop shell — the old `(app)` route group. */}
          <Route element={<AppLayout />}>
            <Route path="/pos" element={<Pos />} />
            <Route path="/warehouse" element={<Warehouse />} />
            <Route path="/suppliers" element={<Suppliers />} />
            <Route path="/credit" element={<Credit />} />
            <Route path="/repairs" element={<Repairs />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/accounting" element={<Accounting />} />
            <Route path="/staff" element={<Staff />} />
            <Route path="/tools" element={<Tools />} />
            <Route path="/guide" element={<Guide />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/terminal" element={<Terminal />} />
          </Route>
        </Route>

        {/* `app/page.tsx` redirected to /warehouse; so does this. */}
        {/* A visitor gets the public page; a signed-in till goes to work. */}
        <Route path="/" element={<Home />} />
        {/* 1.69 — a stray /t (an old service worker still answering with this shell) goes to the guests' menu host */}
        <Route path="/t" element={<ToMenu />} />
        <Route path="*" element={<Navigate to="/warehouse" replace />} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  )
}

/** 1.69 — the guests' table menu lives on its own host; the POS never shows it. */
function ToMenu() {
  useEffect(() => { window.location.replace(`${MENU_ORIGIN}/t${window.location.search}`) }, [])
  return null
}
